import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@environments/environment';
import {
  emailCoverageApiResponse,
  geographicThreatsApiResponse,
  eventsPendingReviewApiResponse,
  configStatusApiResponse,
  emailAverageCoverageApiResponse,
  breakthroughIncidentsAnalysisResponse,
  pendingReviewItemsApiResponse,
  configurationStatusDetailResponse,
} from '../sample/es-sample-data';
import { ODataService } from 'src/app/core/services/odata.service';
import { ConfigurationStatusDetailResponse } from 'src/app/core/models/analytics/es-models';

@Injectable()
export class EsAnalyticsService extends ODataService<any> {
  // TEMPORARY: Local state management for prototyping with mock data
  // TODO: Remove when real API endpoints are implemented - backend will handle state persistence
  private configurationState: ConfigurationStatusDetailResponse | null = null;

  constructor() {
    const url = `${environment.apiUrl}/v1/email-security/analytics/`;
    super(inject(HttpClient), url);
    this.state = {
      skip: 0,
      take: 10,
      sort: [{ field: 'name', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };
  }

  private buildUrl(endpoint: string, organizationId: number, params: any = {}) {
    // TODO: Replace with actual API path when endpoints are ready
    let url = `${environment.apiUrl}/v1/email-security/analytics/${endpoint}(${organizationId})/`;
    let httpParams = new HttpParams();
    for (const key in params) {
      if (params.hasOwnProperty(key) && params[key] !== null) {
        httpParams = httpParams.set(key, params[key]);
      }
    }

    return { url, params: httpParams };
  }

  // API Methods for ES data
  public getEmailCoverageData(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('partnerTicketTrends', organizationId, params);
    // return this.http.get<PartnerTicketTrendsResponse>(url, { params: httpParams });
    return of(emailCoverageApiResponse);
  }

  public getEmailAverageCoverageData(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('partnerTicketTrends', organizationId, params);
    // return this.http.get<PartnerTicketTrendsResponse>(url, { params: httpParams });
    return of(emailAverageCoverageApiResponse);
  }

  public getEmailFunnelData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('emailFunnel', organizationId, params);
    return this.http.get<EmailFunnelApiResponse>(url, { params: httpParams });
  }

  public getEmailThreatsByTypeData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('threatsByType', organizationId, params);
    return this.http.get<ThreatByTypeApiResponse>(url, { params: httpParams });
  }

  public getTopAttackedUsersData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('topAttackedUsers', organizationId, params);
    return this.http.get<TopAttackedUsersApiResponse>(url, { params: httpParams });
  }

  public getGeographicThreatsData(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('layeredProtection', organizationId, params);
    // return this.http.get<LayeredProtectionResponse>(url, { params: httpParams });
    return of(geographicThreatsApiResponse);
  }

  public getEventsPendingReviewData(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('eventsPendingReview', organizationId, params);
    // return this.http.get<EventsPendingReviewResponse>(url, { params: httpParams });
    return of(eventsPendingReviewApiResponse);
  }

  public getConfigStatusData(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('configStatus', organizationId, params);
    // return this.http.get<ConfigStatusResponse>(url, { params: httpParams });
    
    // TEMPORARY: Calculate dynamic status based on current configuration state
    // TODO: Remove when real API is implemented - backend will return calculated status
    const detailedResponse = this.configurationState || configurationStatusDetailResponse;
    const dynamicStatus = this.calculateConfigurationStatus(detailedResponse.compliancePercentage);
    
    const response = {
      ...configStatusApiResponse,
      config_status: dynamicStatus,
      last_updated: detailedResponse.lastUpdated
    };
    
    return of(response);
  }

  public getThreatCampaignData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('threatCampaign', organizationId, params);
    return this.http.get<ThreatCampaignApiResponse>(url, { params: httpParams });
  }

  public getCheckpointProtectionData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('checkpointProtection', organizationId, params);
    return this.http.get<CheckpointProtectionResponse>(url, { params: httpParams });
  }

  public getShadowItTrendsData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('shadowItTrends', organizationId, params);
    return this.http.get<ShadowItTrendsApiResponse>(url, { params: httpParams });
  }

  public getBreakthroughIncidentsAnalysis(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('breakthroughIncidentsAnalysis', organizationId, params);
    // return this.http.get<BreakthroughIncidentsAnalysisResponse>(url, { params: httpParams });
    return of(breakthroughIncidentsAnalysisResponse);
  }

  public getPendingReviewItems(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('pendingReviewItems', organizationId, params);
    // return this.http.get<PendingReviewItemsApiResponse>(url, { params: httpParams });
    return of(pendingReviewItemsApiResponse);
  }

  public getConfigurationDetails(organizationId: number | null, params?: any) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('configurationDetails', organizationId, params);
    // return this.http.get<ConfigurationStatusDetailResponse>(url, { params: httpParams });
    
    // TEMPORARY: Use local state if available, otherwise use mock data
    // TODO: Remove when real API is implemented
    const response = this.configurationState || configurationStatusDetailResponse;
    return of(response);
  }

  public updateConfigurationDeviation(organizationId: number | null, itemId: string, accepted: boolean, note?: string) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('updateConfigurationDeviation', organizationId);
    // const body = { itemId, accepted, note };
    // return this.http.put<ConfigurationStatusDetailResponse>(url, body, { params: httpParams });
    
    // TEMPORARY: Simulate a successful update and store in local state for prototyping
    // TODO: Remove when real API is implemented - backend will handle persistence
    const currentState = this.configurationState || { ...configurationStatusDetailResponse };
    const updatedResponse = { ...currentState };
    const itemIndex = updatedResponse.configurationItems.findIndex(item => item.id === itemId);
    
    if (itemIndex !== -1) {
      updatedResponse.configurationItems[itemIndex] = {
        ...updatedResponse.configurationItems[itemIndex],
        isDeviationAccepted: accepted,
        exceptionNote: note,
        lastUpdated: new Date().toISOString()
      };
      
      // Recalculate compliance percentage
      const totalItems = updatedResponse.configurationItems.length;
      const compliantItems = updatedResponse.configurationItems.filter(
        item => item.currentStatus === 'compliant' || item.isDeviationAccepted
      ).length;
      updatedResponse.compliancePercentage = Math.round((compliantItems / totalItems) * 100);
      
      // Update overall status based on compliance percentage
      updatedResponse.overallStatus = this.calculateConfigurationStatus(updatedResponse.compliancePercentage);
      
      updatedResponse.lastUpdated = new Date().toISOString();
      
      // TEMPORARY: Store updated state locally for prototyping
      // TODO: Remove when real API is implemented
      this.configurationState = updatedResponse;
    }
    
    return of(updatedResponse);
  }

  public executeThreatAction(organizationId: number | null, actionRequest: ActionExecutionRequest) {
    // TODO: Replace with actual API call when endpoints are ready
    // const { url, params: httpParams } = this.buildUrl('executeThreatAction', organizationId);
    // return this.http.post<ActionExecutionResponse>(url, actionRequest, { params: httpParams });
    
    // TEMPORARY: Simulate successful action execution
    // TODO: Remove when real API is implemented
    const response: ActionExecutionResponse = {
      success: true,
      message: `Successfully executed ${actionRequest.actionType} for item ${actionRequest.itemId}`,
      itemId: actionRequest.itemId,
      actionType: actionRequest.actionType,
      timestamp: new Date().toISOString()
    };
    
    return of(response);
  }

  // Helper function to calculate configuration status based on compliance percentage
  private calculateConfigurationStatus(compliancePercentage: number): string {
    if (compliancePercentage === 100) {
      return 'Fully Configured';
    } else if (compliancePercentage >= 75) {
      return 'Mostly Configured';
    } else if (compliancePercentage >= 25) {
      return 'Partially Configured';
    } else {
      return 'Configuration Required';
    }
  }
}

export interface EmailCoverageApiResponse {
  current_coverage_percentage: number;
  previous_coverage_percentage: number;
  coverage_percentage_change: number;
  last_updated: string;
}

export interface EmailFunnelApiResponse {
  total_email_volume: number;
  total_email_volume_previous: number;
  total_email_volume_pct_change: number | null;
  total_threats_detected: number;
  total_threats_detected_previous: number;
  total_threats_detected_pct_change: number | null;
  total_threats_blocked: number;
  total_threats_blocked_previous: number;
  total_threats_blocked_pct_change: number | null;
  total_breakthrough_incidents: number;
  total_breakthrough_incidents_previous: number;
  total_breakthrough_incidents_pct_change: number | null;
}

export interface ThreatByTypeApiResponse {
  total_security_events: number;
  security_events: Array<{ value: string; count: number }>;
}

export interface TopAttackedUsersApiResponse {
  topAttackedUsers: Array<{ name: string; title: string; department: string; attackCount: number }>;
}

export interface GeographicThreatsApiResponse {
  geographicThreats: Array<{ name: string; count: number }>;
}

export interface EventsPendingReviewApiResponse {
  total_events_pending_review: number;
  total_events_pending_review_previous: number;
  total_events_pending_review_change: number;
  last_updated: string;
}

export interface ConfigStatusApiResponse {
  config_id: number;
  config_status: string;
  last_updated: string;
}

export interface EmailAverageCoverageApiResponse {
  average_coverage_percentage: number;
  average_coverage_percentage_previous: number;
  average_coverage_percentage_change: number;
}

export interface ThreatCampaignApiResponse {
  total_suspicious_senders: number;
  top_suspicious_sender: string | null;
  top_sender_volume: number;
  indicators: string[];
  top_sender_velocity?: number;
  top_sender_role_spread?: number;
  top_suspicious_senders: Array<{ sender: string; volume: number }>;
  last_updated: string | null;
}

export interface CheckpointProtectionResponse {
  chartData: Array<{ name: string; value: number }>;
}

export interface ShadowItTrendsApiResponse {
  categoryBreakdown: Array<{ category: string; percentage: number }>;
  trendPercentageChange: number | null;
  chartData: {
    categories: string[];
    series: Array<{ name: string; data: number[] }>;
  };
}

export interface BreakthroughIncidentsAnalysisResponse {
  total_breakthrough_incidents: number;
  percentage_change: number;
  sender_domain_patterns: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  subject_line_patterns: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  attachment_type_patterns: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  recommended_actions: Array<{
    priority: string;
    description: string;
    estimated_impact: string;
    implementation_effort: string;
  }>;
}

export interface PendingReviewItem {
  id: number;
  eventName: string;
  severity: string;
  dateTime: string;
  threatType: string;
  user: string;
  mailbox: string;
  details: string;
  status: string;
  available_actions: string[];
}

export interface PendingReviewItemsApiResponse {
  items: PendingReviewItem[];
  total_count: number;
  last_updated: string;
}

export interface ActionExecutionRequest {
  itemId: number;
  actionType: string;
  parameters?: { [key: string]: any };
  notes?: string;
}

export interface ActionExecutionResponse {
  success: boolean;
  message: string;
  itemId: number;
  actionType: string;
  timestamp: string;
  data?: any;
}
